/* eslint-disable max-lines-per-function */
import { useMemo, useRef } from 'react';
import { GridRows } from '@visx/grid';
import { useTranslation } from 'react-i18next';
import { BelegungsIntervall } from '@/domain/dienstplan/shared/types/Belegung';
import { scaleLinear } from '@visx/scale';
import { useStandortContext } from '@/features/standortContext/StandortContext';
import { useArbeitsbereiche } from '@/domain/arbeitszeiten/hooks';
import { generateChartData } from '@/domain/dienstplan/features/StackedChart/utils/generateChartData';
import { getColorsForMarkierung } from '@/domain/dienstplan/shared/utils/getColorForMarkierung';
import {
  MARGIN,
  MITARBEITER_THRESHOLD,
  STUNDEN_MAX,
} from '@/domain/dienstplan/shared/utils/constants';
import { Polygon } from '@visx/shape';
import {
  AxisBottom,
  AxisLeft,
} from '@/domain/dienstplan/features/StackedChart/Axis';
import { calculateMaxMitarbeiterAnzahl } from '@/domain/dienstplan/features/StackedChart/utils/calculateMaxMitarbeiterAnzahl';
import { StackedAreaChartLegend } from '@/domain/dienstplan/features/StackedChart/Legend';
import { useParentSize } from '@visx/responsive';

export function StackedBelegungenChart({
  belegungen,
}: {
  belegungen: BelegungsIntervall[];
}) {
  const svgRef = useRef<SVGSVGElement>(null);
  const { t } = useTranslation();
  const { parentRef, width, height } = useParentSize();

  const {
    currentStandort: { id: standortId },
  } = useStandortContext();
  const { arbeitsbereiche } = useArbeitsbereiche(standortId);

  const xMax = width - MARGIN.left - MARGIN.right;
  const yMax = height - MARGIN.top - MARGIN.bottom;

  const chartData = useMemo(
    () => generateChartData(belegungen, arbeitsbereiche ?? []),
    [belegungen, arbeitsbereiche],
  );

  let maxMitarbeiterAnzahl = calculateMaxMitarbeiterAnzahl(chartData);
  if (maxMitarbeiterAnzahl < 10) {
    maxMitarbeiterAnzahl = 10;
  }

  const xScalePoint = scaleLinear({
    domain: [0, STUNDEN_MAX],
    range: [MARGIN.left, width - MARGIN.right],
  });

  const yScalePoint = scaleLinear({
    domain: [0, maxMitarbeiterAnzahl + MITARBEITER_THRESHOLD],
    range: [height - MARGIN.bottom, MARGIN.top],
  });

  const yScaleAxis = scaleLinear({
    domain: [0, maxMitarbeiterAnzahl + MITARBEITER_THRESHOLD],
    range: [yMax, 0],
  });

  const xScaleAxis = scaleLinear({
    domain: [0, STUNDEN_MAX],
    range: [0, xMax],
  });

  function getColorByArbeitsbereichId(id: string) {
    const arbeitsbereich = arbeitsbereiche?.find((ab) => ab.id === id);
    if (!arbeitsbereich) {
      return { light: '#000000', base: '#000000' };
    }
    return getColorsForMarkierung(arbeitsbereich.markierung);
  }

  return (
    <div className="flex flex-col gap-2 w-full bg-white rounded-md items-center justify-center">
      <div ref={parentRef} className="w-full h-150">
        <div className="relative" style={{ width, height }}>
          <svg
            shapeRendering="crispEdges"
            ref={svgRef}
            width={width}
            height={height}
            role="img"
            aria-label={t(
              'domain.dienstplan.mindestbelegung.visualEditor.labels.ariaLabel',
            )}>
            <GridRows
              left={MARGIN.left}
              top={MARGIN.top}
              width={xMax}
              height={yMax}
              scale={yScaleAxis}
            />
            <AxisLeft
              yScaleAxis={yScaleAxis}
              maxMitarbeiterAnzahl={
                maxMitarbeiterAnzahl + MITARBEITER_THRESHOLD + 1
              }
            />
            <AxisBottom yMax={yMax} xScaleAxis={xScaleAxis} />
            {Object.keys(chartData).map((key) => {
              return chartData[key].map((points, index) => (
                <Polygon
                  key={key + '_' + index}
                  points={points.map((p) => [
                    xScalePoint(p.x),
                    yScalePoint(p.y),
                  ])}
                  fill={getColorByArbeitsbereichId(key).base}
                />
              ));
            })}
          </svg>
        </div>
      </div>
      <StackedAreaChartLegend belegungen={belegungen} />
    </div>
  );
}
